/**
 * @file The calm screen for an account that is a RESIDENT and nothing else.
 *
 * ## Why it exists
 *
 * `workspace_memberships.role` has four values and two of them — `tenant`, `guarantor` —
 * may not reach a single landlord route (`LANDLORD_ROLES` in `@neighboros/shared`;
 * `apps/api/src/authz.ts`). Nothing in the product can create such a membership yet, but
 * the tenant portal (T1.7) will, and the day it does a person signed in with one of them
 * must not land in the landlord shell: every screen there would open with a 403, and a
 * wall of "couldn't load" panels is not an explanation.
 *
 * So `WorkspaceGate` sends a signed-in person only to a workspace where they hold a
 * landlord role, and when they have workspaces but no such role it renders this instead.
 * A direct link into a resident workspace (`/workspaces/<id>/…`) is bounced to `/` by the
 * frame, which lands here by the same rule.
 *
 * ## What it says, and what it does not offer
 *
 * Three sentences and one action. It does NOT offer to create a workspace (that would turn
 * a resident into a landlord by accident, and the gate's create form is for a person with
 * no workspace at all), and it does NOT link to Account & team: that screen lives under a
 * workspace route and reads the workspace's roster and settings, both of which refuse a
 * resident, so the link would lead straight back to the problem this screen avoids. Sign
 * out is here, not only in the header, because this is the one screen with nothing else to
 * do — the way out should be in front of the person, not in the corner.
 */
import { useQueryClient } from "@tanstack/react-query";
import { Home } from "lucide-react";
import { useAuthValue } from "../auth/context";
import { Card, SecondaryButton } from "./ui";

export const RESIDENT_SCREEN_TITLE = "This account is set up as a resident";

export function NoLandlordAccessScreen() {
  const { actions } = useAuthValue();
  const queryClient = useQueryClient();

  return (
    <div className="mx-auto w-full max-w-xl px-4 py-8">
      <Card className="items-start gap-3">
        <Home className="size-6 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-2xl font-semibold tracking-tight">{RESIDENT_SCREEN_TITLE}</h1>
        <p className="text-sm text-muted-foreground">
          The resident portal is not available yet. Your landlord manages this workspace.
        </p>
        <p className="text-sm text-muted-foreground">
          There is nothing to set up on your side. When the resident portal opens, this
          sign-in will take you to it.
        </p>
        <div className="pt-2">
          <SecondaryButton
            onClick={() => {
              void actions.signOut().then(() => {
                // The next person to sign in on this tab must not see this one's cached /me.
                queryClient.clear();
              });
            }}
          >
            Sign out
          </SecondaryButton>
        </div>
      </Card>
    </div>
  );
}
